import { useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import Modal from "../layout/Modal";
import MultiSelect from "../layout/MultiSelect";
import Button from "../layout/Button";
import useFormValidation from "../../hooks/formValidation";
import {
  WhatsappContact,
  WhatsappGroup,
  addGroupParticipant,
  removeGroupParticipant,
  addGroupAccess,
  removeGroupAccess,
} from "../../model/whatsapp.model";
import { brPhoneVariants, formatPhoneDisplay, isContactAlreadyParticipant } from "../whatsapp/format";

type Option = { value: string; label: string };
// Mesmo tipo local de Whatsapp.page.tsx (não importado de lá — importar de
// volta criaria dependência circular, a página é quem importa este modal).
type Extension = { value: string; name: string };

function ManageParticipantsModal(props: {
  group: WhatsappGroup;
  contacts: WhatsappContact[];
  extensions: Extension[];
  onClose: () => void;
}) {
  const [selectedContacts, setSelectedContacts] = useState<Option[]>([]);
  const [adding, setAdding] = useState(false);
  const [removingPhone, setRemovingPhone] = useState<string | null>(null);
  const formValidation = useFormValidation();

  const [selectedAgents, setSelectedAgents] = useState<Option[]>([]);
  const [addingAgent, setAddingAgent] = useState(false);
  const [removingAgentId, setRemovingAgentId] = useState<string | null>(null);
  const agentFormValidation = useFormValidation();

  // Bug real corrigido (2026-09-21): comparação direta (sem brPhoneVariants)
  // deixava um contato já membro do grupo continuar aparecendo como
  // "disponível" pra adicionar de novo — telefone do participante vem do
  // JID real do WhatsApp (pode não ter o 9º dígito), enquanto o telefone
  // salvo no contato normalmente tem. Mesma divergência que já é tratada
  // logo abaixo (nameForPhone) pra achar o nome do participante — só
  // faltava aplicar aqui também, pro filtro de "quem já é membro".
  const participantPhones = props.group.participants.map((p) => p.phone);
  const contactOptions: Option[] = props.contacts
    .filter((c) => !isContactAlreadyParticipant(c.phone, participantPhones))
    .map((c) => ({ value: c.id, label: `${c.name} (${formatPhoneDisplay(c.phone)})` }));

  const onAdd = async () => {
    if (selectedContacts.length === 0 || adding) return;
    setAdding(true);
    const result = await addGroupParticipant({
      groupId: props.group.id,
      contactIds: selectedContacts.map((o) => o.value),
    });
    setAdding(false);
    if (result.error) {
      toast.error("Não foi possível adicionar participante(s).");
      return;
    }
    setSelectedContacts([]);
  };

  const onRemove = async (phone: string) => {
    if (!window.confirm("Remover este participante do grupo?")) return;
    setRemovingPhone(phone);
    const result = await removeGroupParticipant({ groupId: props.group.id, phone });
    setRemovingPhone(null);
    if (result.error) toast.error("Não foi possível remover o participante.");
  };

  const contactByPhone = new Map(props.contacts.map((c) => [c.phone, c]));

  // Acesso DENTRO do Voki (cada atendente com a própria conta) — não tem
  // nada a ver com os participantes reais do WhatsApp acima, que é sempre
  // só o número da instância. Vazio no grupo = aberto pra empresa inteira.
  const currentAccessIds = new Set(props.group.accessUserIds || []);
  const agentOptions: Option[] = props.extensions
    .filter((e) => !currentAccessIds.has(e.value))
    .map((e) => ({ value: e.value, label: e.name }));
  const extensionById = new Map(props.extensions.map((e) => [e.value, e]));

  const onAddAgent = async () => {
    if (selectedAgents.length === 0 || addingAgent) return;
    setAddingAgent(true);
    const results = await Promise.all(
      selectedAgents.map((o) => addGroupAccess({ groupId: props.group.id, userId: o.value })),
    );
    setAddingAgent(false);
    if (results.some((r) => r.error)) {
      toast.error("Não foi possível dar acesso a todos os atendentes selecionados.");
      return;
    }
    setSelectedAgents([]);
  };

  const onRemoveAgent = async (userId: string) => {
    if (!window.confirm("Remover o acesso desse atendente a este grupo?")) return;
    setRemovingAgentId(userId);
    const result = await removeGroupAccess({ groupId: props.group.id, userId });
    setRemovingAgentId(null);
    if (result.error) toast.error("Não foi possível remover o acesso.");
  };

  return (
    <Modal shown title="Grupo" onClose={props.onClose} className="w-[440px]">
      <div className="text-[11px] font-bold text-gray-dark uppercase mb-1.5">
        Atendentes com acesso
      </div>
      <p className="text-[10.5px] text-gray-dark mb-2">
        {currentAccessIds.size === 0
          ? "Nenhum atendente adicionado ainda — por enquanto, qualquer atendente da empresa consegue ver e usar este grupo. Adicionar alguém aqui restringe o acesso só a quem estiver na lista."
          : "Só os atendentes abaixo conseguem ver e usar este grupo."}
      </p>
      <div className="max-h-[140px] overflow-y-auto mb-3">
        {(props.group.accessUserIds || []).map((userId) => (
          <div
            key={userId}
            className="flex items-center justify-between gap-2 px-2 py-2 rounded-10 hover:bg-white-dark"
          >
            <div className="font-bold text-xs text-primary truncate">
              {extensionById.get(userId)?.name || userId}
            </div>
            <button
              onClick={() => onRemoveAgent(userId)}
              disabled={removingAgentId === userId}
              title="Remover acesso"
              className="flex-none w-7 h-7 rounded-10 text-gray-dark hover:bg-red/10 hover:text-red grid place-items-center text-xs disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </button>
          </div>
        ))}
      </div>
      <div className="mb-2">
        <MultiSelect
          title="Adicionar atendente"
          options={agentOptions}
          value={selectedAgents}
          //@ts-ignore
          setter={setSelectedAgents}
          formValidation={agentFormValidation}
        />
      </div>
      <div className="flex justify-end mb-5">
        <Button
          onClick={onAddAgent}
          disabled={selectedAgents.length === 0 || addingAgent}
          isLoading={addingAgent}
        >
          Adicionar
        </Button>
      </div>

      <hr className="border-t-2 border-white-dark mb-5" />

      <div className="text-[11px] font-bold text-gray-dark uppercase mb-1.5">
        Participantes do grupo (WhatsApp)
      </div>
      <div className="max-h-[160px] overflow-y-auto mb-4">
        {props.group.participants.map((p) => {
          // Telefone do participante vem do JID real do WhatsApp (ver
          // backend), que pode divergir do telefone digitado no contato
          // salvo só pelo 9º dígito (número brasileiro) — testa as duas
          // variantes pra não perder o match.
          const contact = brPhoneVariants(p.phone)
            .map((v) => contactByPhone.get(v))
            .find(Boolean);
          return (
            <div
              key={p.phone}
              className="flex items-center justify-between gap-2 px-2 py-2 rounded-10 hover:bg-white-dark"
            >
              <div className="min-w-0">
                <div className="font-bold text-xs text-primary truncate">
                  {contact?.name || p.name || formatPhoneDisplay(p.phone)}
                  {p.isAdmin && (
                    <span className="ml-1.5 text-[9.5px] font-bold text-purple">admin</span>
                  )}
                </div>
                <div className="text-[11px] text-gray-dark">{formatPhoneDisplay(p.phone)}</div>
              </div>
              <button
                onClick={() => onRemove(p.phone)}
                disabled={removingPhone === p.phone}
                title="Remover do grupo"
                className="flex-none w-7 h-7 rounded-10 text-gray-dark hover:bg-red/10 hover:text-red grid place-items-center text-xs disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            </div>
          );
        })}
        {props.group.participants.length === 0 && (
          <div className="text-center text-xs text-gray-dark py-4">Nenhum participante.</div>
        )}
      </div>

      <div className="mb-2">
        <MultiSelect
          title="Adicionar participante"
          options={contactOptions}
          value={selectedContacts}
          //@ts-ignore
          setter={setSelectedContacts}
          formValidation={formValidation}
        />
      </div>

      <div className="flex justify-end gap-2.5 mt-5">
        <Button outline color="gray" onClick={props.onClose}>
          Fechar
        </Button>
        <Button
          onClick={onAdd}
          disabled={selectedContacts.length === 0 || adding}
          isLoading={adding}
        >
          Adicionar
        </Button>
      </div>
    </Modal>
  );
}

export default ManageParticipantsModal;
